import { useEffect, useState, type FormEvent } from "react";
import {
  api,
  ApiError,
  DELETE_ACCOUNT_CONFIRMATION,
  type MeResponse,
  type RetentionChangeEntry,
  type RetentionState
} from "../api/client";
import { InfoTooltip } from "../components/Tooltip";

function formatDate(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  return new Date(parsed).toISOString().slice(0, 10);
}

function formatChangeType(t: RetentionChangeEntry["change_type"]): string {
  switch (t) {
    case "bulk_extend":
      return "Bulk extend";
    case "bulk_shorten":
      return "Bulk shorten";
    case "default_change":
      return "Default change";
  }
}

function formatRetentionLabel(value: string): string {
  if (!value) return "infinity";
  const t = value.trim().toLowerCase();
  if (t === "infinity" || t === "infinite") return "Indefinite (no auto-prune)";
  return value;
}

export function SettingsPage(props: { onSignOut: () => void }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.me();
        if (!cancelled) setMe(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError("Could not load settings.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error && !me) return <div className="error">{error}</div>;
  if (!me) return <p className="muted">Loading…</p>;

  const { customer } = me;

  return (
    <>
      <div className="panel">
        <h2 className="section-title">Settings</h2>
        <dl>
          <div className="row">
            <dt>
              Email
              <InfoTooltip content="Email is managed by your auth provider. To change it, update your profile on the sign-in page." />
            </dt>
            <dd>
              {customer.email}
              <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                managed by your auth provider
              </span>
            </dd>
          </div>
          <div className="row">
            <dt>Account created</dt>
            <dd>{formatDate(customer.created_at)}</dd>
          </div>
          <div className="row">
            <dt>
              Account ID
              <InfoTooltip content="Include this opaque ID in any support ticket so we can look up your account quickly." />
            </dt>
            <dd>{customer.opaque_id}</dd>
          </div>
          <div className="row">
            <dt>Account status</dt>
            <dd>{customer.account_status}</dd>
          </div>
        </dl>
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={props.onSignOut}>
            Sign out
          </button>
        </div>
      </div>
      <RetentionSection />
      <DangerZoneSection onSignOut={props.onSignOut} />
    </>
  );
}

// §3.1 Retention PR 3: account-deletion danger zone. Customer-
// initiated GDPR account deletion is immediate and irreversible.
// No tombstone grace exists on this path (the prune cron's 365-day
// grace is for automatic retention, not customer-initiated GDPR
// deletion per spec D6/D7). Make that unmistakable to the customer
// before they confirm.
function DangerZoneSection(props: { onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await api.deleteAccount(typed);
      // Sign-out routine clears the session token; the customer's
      // server-side session row is gone with the cascade so the
      // local token is now invalid anyway.
      props.onSignOut();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "account_has_held_receipts") {
          setMessage(
            "Account deletion refused: at least one receipt is on legal hold. Contact support to lift the hold first."
          );
        } else if (err.code === "confirmation_mismatch") {
          setMessage("The confirmation string did not match. Type the literal value shown.");
        } else if (err.code === "refused_no_r2_client") {
          setMessage(
            "Account deletion is temporarily unavailable (storage backend not reachable). Try again later."
          );
        } else {
          setMessage(err.message);
        }
      } else {
        setMessage("Could not delete the account.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel" style={{ borderColor: "#f5cccc" }}>
      <h2 className="section-title" style={{ color: "#b00020" }}>
        Danger zone
      </h2>
      <p className="muted">
        Account deletion is immediate and irreversible. Every receipt in your library, every R2
        object Sequesign holds for you, and every linking identifier in the witness log is removed;
        only the witness log entries' cryptographic hashes remain as the integrity anchor for
        everyone else's proofs. API keys, sessions, and billing identifiers are cascade-deleted with
        your account.
      </p>
      <p className="muted">
        Receipts on legal hold cannot be deleted; the entire account-deletion request will be
        refused until those holds are lifted.
      </p>
      {!open ? (
        <button
          className="btn btn-secondary"
          style={{ color: "#b00020", borderColor: "#b00020" }}
          onClick={() => setOpen(true)}
        >
          Delete my account...
        </button>
      ) : (
        <form onSubmit={submit}>
          <p style={{ fontSize: 12, marginBottom: 4 }}>
            To confirm, type <code>{DELETE_ACCOUNT_CONFIRMATION}</code> exactly:
          </p>
          <div className="row" style={{ alignItems: "center" }}>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={DELETE_ACCOUNT_CONFIRMATION}
              style={{ flex: 1, marginRight: 8 }}
              autoComplete="off"
              spellCheck={false}
            />
            <button
              className="btn"
              type="submit"
              disabled={busy || typed !== DELETE_ACCOUNT_CONFIRMATION}
              style={{
                background: typed === DELETE_ACCOUNT_CONFIRMATION ? "#b00020" : undefined,
                color: typed === DELETE_ACCOUNT_CONFIRMATION ? "white" : undefined
              }}
            >
              {busy ? "Deleting..." : "Delete account"}
            </button>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => {
                setOpen(false);
                setTyped("");
                setMessage(null);
              }}
              style={{ marginLeft: 8 }}
            >
              Cancel
            </button>
          </div>
          {message !== null && (
            <div className="error" style={{ marginTop: 8 }}>
              {message}
            </div>
          )}
        </form>
      )}
    </div>
  );
}

function RetentionSection() {
  const [state, setState] = useState<RetentionState | null>(null);
  const [history, setHistory] = useState<RetentionChangeEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newDefault, setNewDefault] = useState<string>("");
  const [applyToExisting, setApplyToExisting] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<string | null>(null);

  async function reload(): Promise<void> {
    try {
      const [s, h] = await Promise.all([api.getRetentionState(), api.getRetentionHistory()]);
      setState(s);
      setHistory(h.changes);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not load retention settings.");
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function submitDefault(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const result = await api.changeRetentionDefault({
        new_default: newDefault,
        apply_to_existing: applyToExisting
      });
      setSubmitResult(
        applyToExisting
          ? `Default updated. ${result.affected} existing receipt(s) held forward to the new window. Receipts whose remaining retention was already longer than the new window were left unchanged; this action can only hold or extend an existing receipt, never shorten it early.`
          : "Default updated. Existing receipts keep their stamped retention; future receipts use the new default."
      );
      setNewDefault("");
      setApplyToExisting(false);
      await reload();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "retention_below_tier_floor") {
          setSubmitResult(
            `Error: ${err.message}. To remove specific receipts sooner than the floor allows, contact support.`
          );
        } else if (err.code === "retention_above_tier_ceiling") {
          setSubmitResult(
            `Error: ${err.message}. Upgrade to a paid tier for configurable retention.`
          );
        } else {
          setSubmitResult(`Error: ${err.message}`);
        }
      } else {
        setSubmitResult("Could not update default retention.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (error && !state) {
    return (
      <div className="panel">
        <h2 className="section-title">Retention</h2>
        <div className="error">{error}</div>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="panel">
        <h2 className="section-title">Retention</h2>
        <p className="muted">Loading…</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <h2 className="section-title">Retention</h2>
      <dl>
        <div className="row">
          <dt>
            Current default
            <InfoTooltip content="New receipts use this retention window. Free-tier customers default to 90 days; paid tiers default to indefinite." />
          </dt>
          <dd>{formatRetentionLabel(state.default_retention)}</dd>
        </div>
        <div className="row">
          <dt>Active receipts</dt>
          <dd>{state.total_active_receipts}</dd>
        </div>
        <div className="row">
          <dt>
            Pruning within 7 days
            <InfoTooltip content="Receipts scheduled to tombstone in the next 7 days. Extend them individually or extend the default to keep them." />
          </dt>
          <dd>
            {state.upcoming_prunes_next_7d}
            {state.upcoming_prunes_next_7d > 0 && (
              <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                Open the Receipts page to extend individually.
              </span>
            )}
          </dd>
        </div>
        <div className="row">
          <dt>Tombstoned receipts</dt>
          <dd>{state.tombstoned_receipts}</dd>
        </div>
      </dl>
      <form onSubmit={submitDefault} style={{ marginTop: 16 }}>
        <h3 className="section-title" style={{ fontSize: 14 }}>
          Change default retention
        </h3>
        <p className="muted" style={{ fontSize: 12 }}>
          Accepts Postgres INTERVAL strings (e.g. <code>90 days</code>, <code>1 year</code>, or{" "}
          <code>infinity</code>). The change applies to future receipts by default. Free-tier
          retention is fixed at 90 days (floor and ceiling); configurable retention, including{" "}
          <code>infinity</code>, is available on paid tiers. Paid tiers have no floor or ceiling.
        </p>
        <p className="muted" style={{ fontSize: 12 }}>
          The &quot;Apply to existing receipts&quot; option extends or holds existing receipts to a
          full new window measured from today. It never shortens a receipt early. To remove specific
          receipts sooner than their committed window, contact support.
        </p>
        <div className="row" style={{ alignItems: "center" }}>
          <input
            type="text"
            value={newDefault}
            onChange={(e) => setNewDefault(e.target.value)}
            placeholder="e.g. 90 days"
            style={{ flex: 1, marginRight: 8 }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
            <input
              type="checkbox"
              checked={applyToExisting}
              onChange={(e) => setApplyToExisting(e.target.checked)}
            />
            Apply to existing receipts
          </label>
        </div>
        <div style={{ marginTop: 8 }}>
          <button className="btn" type="submit" disabled={submitting || newDefault.length === 0}>
            {submitting ? "Saving..." : "Update default"}
          </button>
        </div>
        {submitResult !== null && (
          <div
            className={submitResult.startsWith("Error") ? "error" : "muted"}
            style={{ marginTop: 8 }}
          >
            {submitResult}
          </div>
        )}
      </form>
      <div style={{ marginTop: 24 }}>
        <h3 className="section-title" style={{ fontSize: 14 }}>
          Recent changes
        </h3>
        {history.length === 0 ? (
          <p className="muted">No retention changes recorded yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Applied</th>
                <th>Type</th>
                <th>Receipts affected</th>
                <th>New default</th>
              </tr>
            </thead>
            <tbody>
              {history.map((change) => (
                <tr key={change.change_id}>
                  <td>{formatDate(change.applied_at)}</td>
                  <td>{formatChangeType(change.change_type)}</td>
                  <td>{change.receipts_affected}</td>
                  <td>{change.new_default ?? "(n/a)"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
